/**
 * Image Compression Utility
 *
 * Compresses images to WebP format before upload
 * Reduces file size and upload time significantly
 *
 * @package Lovasok_Marketplace
 */

/**
 * Compress image to WebP format
 *
 * @param {File} file - Original image file
 * @param {number} maxWidth - Maximum width (default: 1920px)
 * @param {number} quality - Quality 0-1 (default: 0.85)
 * @returns {Promise<Blob>} Compressed WebP blob
 */
export async function compressImage(file, maxWidth = 1920, quality = 0.85) {
    return new Promise((resolve, reject) => {
        // Check if browser supports WebP
        const canvas = document.createElement('canvas');
        const supportsWebP = canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;

        if (!supportsWebP) {
            console.warn('WebP not supported, using JPEG compression');
        }

        const reader = new FileReader();

        reader.onload = (e) => {
            const img = new Image();

            img.onload = () => {
                // Calculate new dimensions maintaining aspect ratio
                let width = img.width;
                let height = img.height;

                if (width > maxWidth) {
                    height = Math.round((height * maxWidth) / width);
                    width = maxWidth;
                }

                // Create canvas
                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext('2d');

                // Enable image smoothing for better quality
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';

                // Draw image
                ctx.drawImage(img, 0, 0, width, height);

                // Convert to WebP or JPEG
                const mimeType = supportsWebP ? 'image/webp' : 'image/jpeg';

                canvas.toBlob((blob) => {
                    if (blob) {
                        resolve(blob);
                    } else {
                        reject(new Error('Image compression failed'));
                    }
                }, mimeType, quality);
            };

            img.onerror = () => {
                reject(new Error('Failed to load image'));
            };

            img.src = e.target.result;
        };

        reader.onerror = () => {
            reject(new Error('Failed to read file'));
        };

        reader.readAsDataURL(file);
    });
}

/**
 * Check whether browser-side canvas compression is likely supported.
 * HEIC/HEIF typically cannot be decoded in most browsers via canvas.
 *
 * @param {File} file
 * @returns {boolean}
 */
export function canCompressInBrowser(file) {
    if (!file || !file.type) {
        return false;
    }

    const blockedTypes = ['image/heic', 'image/heif'];
    if (blockedTypes.includes(file.type.toLowerCase())) {
        return false;
    }

    return file.type.startsWith('image/');
}

/**
 * Get file size in human readable format
 *
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted size (e.g., "2.5 MB")
 */
export function getFileSizeReduction(originalSize, compressedSize) {
    const reduction = ((originalSize - compressedSize) / originalSize * 100).toFixed(1);
    return {
        reduction: reduction,
        originalMB: (originalSize / 1024 / 1024).toFixed(2),
        compressedMB: (compressedSize / 1024 / 1024).toFixed(2)
    };
}
